import { motion } from "framer-motion";
import { BedDouble, Building2, MessageCircle, CheckCircle2, BadgeCheck } from "lucide-react";
import { waLink, WA_BNB_MSG, WA_CONDO_MSG, WA_SUBSCRIBER_MSG } from "@/lib/site";
import ConvenzioneForm from "@/components/ConvenzioneForm";

const BOXES = [
  {
    id: "bnb",
    icon: BedDouble,
    eyebrow: "Per Gestori B&B / Property Manager",
    title: "Salva le recensioni dei tuoi ospiti.",
    desc: "Se una doccia si blocca alle 21:00, arriviamo prima che lascino 1 stella. Gestione chiavi con codice di sicurezza e fatturazione riepilogativa mensile.",
    features: [
      "Priorità d'uscita serale e festiva",
      "Accesso autonomo con custodia chiavi",
      "Report fotografico prima/dopo per gli host",
      "Unica fattura mensile per tutte le strutture",
    ],
    msg: WA_BNB_MSG,
    testid: "b2b-bnb",
  },
  {
    id: "condominio",
    icon: Building2,
    eyebrow: "Per Amministratori di Condominio",
    title: "Parti comuni senza attese bibliche.",
    desc: "Pompe sommerse, luci scale, portoni condominiali e autoclave: convenzione a canone zero con priorità d'uscita garantita.",
    features: [
      "Canone zero: paghi solo l'intervento",
      "Corsia preferenziale sulle urgenze",
      "Verbali e documentazione per il bilancio",
      "Fatturazione elettronica e detraibilità",
    ],
    msg: WA_CONDO_MSG,
    testid: "b2b-condo",
  },
];

export default function B2B() {
  return (
    <section id="b2b" className="bg-[#0A1329] py-16 sm:py-28" data-testid="b2b-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.22em] text-[#FFB800]">
            03 — Convenzioni B2B
          </p>
          <h2 className="font-display mt-3 text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Il tuo tecnico di fiducia, <span className="text-[#FF5500]">in abbonamento.</span>
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {BOXES.map((b, i) => (
            <motion.div
              key={b.id}
              data-testid={`b2b-card-${b.id === "bnb" ? "bnb-managers" : "condominium-admins"}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="flex flex-col rounded-2xl border border-white/10 bg-[#0D1833] p-6 transition-all duration-300 hover:border-[#FF5500]/45 sm:p-8"
            >
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-[#FF5500]/15">
                <b.icon className="h-7 w-7 text-[#FF5500]" strokeWidth={2} />
              </div>
              <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                {b.eyebrow}
              </p>
              <h3 className="font-display mt-2 text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                {b.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{b.desc}</p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {b.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#25D366]" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={waLink(b.msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${b.testid}-whatsapp-cta`}
                className="mt-7 flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-[#FFB800] font-bold text-[#060B19] transition-all duration-200 hover:scale-[1.02] hover:bg-[#ffc533] active:scale-95"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                Richiedi Convenzione
              </a>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex flex-col items-center gap-3"
        >
          <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Hai già una convenzione attiva?
          </p>
          <a
            href={waLink(WA_SUBSCRIBER_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="b2b-subscriber-cta"
            className="flex min-h-[52px] items-center gap-2 rounded-xl border border-[#FFB800]/50 bg-[#FFB800]/10 px-8 font-bold text-[#FFB800] transition-all duration-200 hover:scale-[1.03] hover:bg-[#FFB800] hover:text-[#060B19] active:scale-95"
          >
            <BadgeCheck className="h-5 w-5" strokeWidth={2.5} />
            Sono abbonato — priorità d'uscita
          </a>
          <p className="max-w-md text-center text-xs text-slate-500">
            Si apre WhatsApp col messaggio già pronto: ti chiediamo solo l'indirizzo e il video del guasto.
          </p>
        </motion.div>

        <ConvenzioneForm />
      </div>
      <div className="hazard-stripe mt-16 sm:mt-28" />
    </section>
  );
}
